import type { LogoSurface } from '~/helpers/branding/studeva-logo'
import { useBranding } from '~/helpers/branding/branding-context'
import { studevaLogo } from '~/helpers/branding/studeva-logo'
import { cn } from '~/lib/utils'

type BrandLogoProps = {
  /** Classe appliquée à l'image du logo. */
  className?: string
  /** Classe appliquée au fallback texte (nom de l'app) si aucun logo. */
  fallbackClassName?: string
  /** Fond sur lequel le logo est posé : décide de la variante du logo Studeva par défaut. */
  surface?: LogoSurface
}

/**
 * Affiche le logo configuré (white-label) ou, à défaut, le nom de l'app en
 * texte. Remplace toute référence à un logo client codé en dur.
 *
 * Deux variantes possibles, claire et sombre (#31) : un logo monochrome foncé
 * disparaît sur fond sombre, et c'est l'apprenant qui choisit son thème — pas
 * l'organisation. Sans second fichier, elle devait donc renoncer à l'un des
 * deux publics.
 *
 * La bascule est faite en CSS (`dark:`) et non en JavaScript : les deux balises
 * sont rendues, le thème n'en montre qu'une. Lire le thème en JS obligerait à
 * s'abonner à ses changements, et surtout ferait clignoter le mauvais logo le
 * temps du premier rendu, au moment précis où l'écran de connexion s'affiche.
 *
 * `object-contain object-left` : posé dans une colonne flex (en-tête des cartes
 * d'authentification), le logo est étiré sur toute la largeur alors que sa
 * hauteur est fixée — l'image se déformait. Ainsi, quelle que soit la boîte
 * que lui donne son parent, le logo garde ses proportions, calé à gauche.
 */
const IMAGE = 'object-contain object-left'

export function BrandLogo({ className, fallbackClassName, surface = 'page' }: BrandLogoProps) {
  const branding = useBranding()
  const { logo, logoDark, appName } = branding

  // Sans aucune personnalisation, le logo Studeva remplace le nom en texte.
  // Une seule variante fournie par l'organisation = elle sert sur les deux
  // fonds : c'est le bon défaut pour un logo bicolore ou détouré, qui tient
  // partout — imposer un second fichier serait du travail sans objet.
  const own = logo || logoDark
  const variants = studevaLogo(branding, surface) ?? (own ? { light: own, dark: logoDark || own } : null)

  if (variants) {
    if (variants.light === variants.dark) {
      return <img src={variants.light} alt={appName} className={cn(IMAGE, className)} />
    }

    return (
      <>
        <img src={variants.light} alt={appName} className={cn(IMAGE, 'dark:hidden', className)} />
        {/* Même `alt` sur les deux : une seule est affichée à la fois (l'autre
            est en `display: none`, donc ignorée des lecteurs d'écran). Vider
            celle du thème sombre laissait le logo sans nom en sombre (RGAA 1.2). */}
        <img src={variants.dark} alt={appName} className={cn(IMAGE, 'hidden dark:block', className)} />
      </>
    )
  }

  return (
    <span className={cn('font-heading font-semibold tracking-tight', fallbackClassName)}>
      {appName}
    </span>
  )
}
